/**
 * Business dashboard routes — owner tools behind ProtectedRoute.
 * All pages are code-split via React.lazy() to keep public bundle lean.
 */
import { lazy, Suspense } from "react";
import { Navigate, Route } from "react-router-dom";
import { ProtectedRoute } from "../components/common";
import DashboardShell from "../components/layouts/DashboardShell";
import { useLocalizedPath } from "../i18n/useLocalizedPath";

// Lazy-loaded dashboard pages
const BusinessDashboardHome                = lazy(() => import("../pages/business/dashboard/home"));
const BusinessDashboardServices            = lazy(() => import("../pages/business/dashboard/services"));
const BusinessDashboardBookings            = lazy(() => import("../pages/business/dashboard/bookings"));
const BusinessDashboardCRM                 = lazy(() => import("../pages/business/dashboard/crm"));
const InboxPage                            = lazy(() => import("../pages/business/dashboard/communication-hub/InboxPage"));
const ThreadDetail                         = lazy(() => import("../pages/business/dashboard/communication-hub/ThreadDetail"));
const ChannelsPage                         = lazy(() => import("../pages/business/dashboard/communication-hub/ChannelsPage"));
const ConnectWhatsApp                      = lazy(() => import("../pages/business/dashboard/communication-hub/ConnectWhatsApp"));
const BusinessDashboardTeamManagement      = lazy(() => import("../pages/business/dashboard/team"));
const MyBusiness360                        = lazy(() => import("../pages/business/dashboard/my-business"));
const ReviewsPage                          = lazy(() => import("../pages/business/dashboard/ReviewsPage"));
const BusinessDashboardCalendar            = lazy(() => import("../pages/business/dashboard/calendar"));
const BusinessDashboardOrders              = lazy(() => import("../pages/business/dashboard/orders"));
const BusinessDashboardAnalytics           = lazy(() => import("../pages/business/dashboard/analytics"));
const BusinessDashboardNotificationsPage   = lazy(() => import("../pages/business/dashboard/notificationsPage"));
const BusinessDashboardDocuments           = lazy(() => import("../pages/business/dashboard/documents"));
const BusinessDashboardInvoice             = lazy(() => import("../pages/business/dashboard/invoice"));
const BusinessDashboardBilling             = lazy(() => import("../pages/business/dashboard/billing"));
const BusinessDashboardSeo                 = lazy(() => import("../pages/business/dashboard/seo"));

function RedirectMyBusiness() {
  const lp = useLocalizedPath();
  return <Navigate to={lp("/dashboard/my-business")} replace />;
}

function RedirectMyBusinessMedia() {
  const lp = useLocalizedPath();
  return <Navigate to={`${lp("/dashboard/my-business")}#media`} replace />;
}

/** Legacy / stub URLs → dashboard home */
function RedirectDashboardHome() {
  const lp = useLocalizedPath();
  return <Navigate to={lp("/dashboard")} replace />;
}

function ChunkLoader() {
  return (
    <div className="flex items-center justify-center min-h-[40vh]">
      <div className="h-10 w-10 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

const lazyRoute = (Element) => (
  <Suspense fallback={<ChunkLoader />}>
    <Element />
  </Suspense>
);

const BusinessRoutes = () => {
  return (
    <>
      <Route
        path="dashboard"
        element={
          <ProtectedRoute requiredRole="business">
            <DashboardShell />
          </ProtectedRoute>
        }
      >
        <Route index                            element={lazyRoute(BusinessDashboardHome)} />
        <Route path="my-business"               element={lazyRoute(MyBusiness360)} />
        <Route path="profile"                   element={<RedirectMyBusiness />} />
        <Route path="services"                  element={lazyRoute(BusinessDashboardServices)} />
        <Route path="bookings"                  element={lazyRoute(BusinessDashboardBookings)} />
        <Route path="calendar"                  element={lazyRoute(BusinessDashboardCalendar)} />
        <Route path="crm"                       element={lazyRoute(BusinessDashboardCRM)} />
        <Route path="media"                     element={<RedirectMyBusinessMedia />} />
        <Route path="inbox"                     element={lazyRoute(InboxPage)} />
        <Route path="inbox/:threadId"           element={lazyRoute(ThreadDetail)} />
        <Route path="channels"                  element={lazyRoute(ChannelsPage)} />
        <Route path="channels/connect-whatsapp" element={lazyRoute(ConnectWhatsApp)} />
        <Route path="team"                      element={lazyRoute(BusinessDashboardTeamManagement)} />
        <Route path="reviews"                   element={lazyRoute(ReviewsPage)} />
        <Route path="seo"                       element={lazyRoute(BusinessDashboardSeo)} />
        {/* Removed stub modules; old bookmarks → home */}
        <Route path="settings/*"                element={<RedirectDashboardHome />} />
        <Route path="orders"                    element={lazyRoute(BusinessDashboardOrders)} />
        <Route path="finance"                   element={lazyRoute(BusinessDashboardDocuments)} />
        <Route path="documents"                 element={lazyRoute(BusinessDashboardDocuments)} />
        <Route path="invoice"                   element={lazyRoute(BusinessDashboardInvoice)} />
        <Route path="analytics"                 element={lazyRoute(BusinessDashboardAnalytics)} />
        <Route path="notifications"             element={lazyRoute(BusinessDashboardNotificationsPage)} />
        <Route path="marketplace"               element={<RedirectDashboardHome />} />
        <Route path="billing"                   element={lazyRoute(BusinessDashboardBilling)} />
        <Route path="dev-tasks"                 element={<RedirectDashboardHome />} />
        <Route path="owner-rooms/*"             element={<RedirectDashboardHome />} />
      </Route>
    </>
  );
};

export default BusinessRoutes;
